/**
 * 从 useAppUpdateStore（zustand）读取更新状态，计算侧边栏和浮层的 UI 元数据。
 * 不管理任何状态，只做读取和派生。
 */

import React from 'react';
import { LoadingOutlined, SyncOutlined } from '@ant-design/icons';
import { Progress } from 'antd';
import type { MessageInstance } from 'antd/es/message/interface';
import type { AppUpdateStatusPayload } from '../../../common/types/appUpdate';
import { useAppUpdateStore } from '../../store/appUpdate';
import { useAppUpdateActions } from '../../hooks/useAppUpdateActions';

export interface SidebarUpdateMeta {
  tooltip: string;
  label: string;
  statusClassName: string;
  rightIcon: React.ReactNode;
  onClick: (() => void) | undefined;
  disabled: boolean;
  ariaLabel: string;
}

export interface ProfileUpdateMeta {
  label: string;
  actionText: string;
  loading: boolean;
  clickable: boolean;
  onClick: (() => void) | undefined;
}

export function useMainLayoutUpdate(
  messageApi: MessageInstance,
  setProfilePopoverOpen: React.Dispatch<React.SetStateAction<boolean>>,
) {
  // 从 zustand store 读取状态
  const pendingUpdateStatus = useAppUpdateStore((s) => s.pendingUpdateStatus);
  const availableUpdateStatus = useAppUpdateStore((s) => s.availableUpdateStatus);
  const downloadingUpdateStatus = useAppUpdateStore((s) => s.downloadingUpdateStatus);
  const updateCheckAuthority = useAppUpdateStore((s) => s.updateCheckAuthority);
  const updateModalOpen = useAppUpdateStore((s) => s.updateModalOpen);
  const updateActionSubmitting = useAppUpdateStore((s) => s.updateActionSubmitting);
  const manualUpdateChecking = useAppUpdateStore((s) => s.manualUpdateChecking);
  const setUpdateModalOpen = useAppUpdateStore((s) => s.setUpdateModalOpen);
  const { handleUpdatePrimaryAction, handleManualCheckUpdate } = useAppUpdateActions(messageApi);

  // 派生状态
  const hasServerConfirmedUpdate = updateCheckAuthority === 'has-update';
  const visiblePendingUpdateStatus = hasServerConfirmedUpdate ? pendingUpdateStatus : null;
  const effectiveUpdateStatus: AppUpdateStatusPayload | null =
    visiblePendingUpdateStatus ?? downloadingUpdateStatus ?? availableUpdateStatus;
  const sidebarUpdateStatus = hasServerConfirmedUpdate
    ? (visiblePendingUpdateStatus ?? downloadingUpdateStatus ?? availableUpdateStatus)
    : null;

  // 侧边栏更新入口元数据（1:1 复制原始 MainLayout 逻辑）
  const sidebarUpdateMeta: SidebarUpdateMeta | null = (() => {
    if (!sidebarUpdateStatus) {
      return null;
    }

    if (sidebarUpdateStatus.status === 'downloaded') {
      return {
        tooltip: sidebarUpdateStatus.latestVersion
          ? `新版本 ${sidebarUpdateStatus.latestVersion} 已下载完成`
          : '更新包已下载完成',
        label: '重启后完成更新',
        statusClassName: 'main-layout__bottom-card--update-downloaded',
        rightIcon: null as React.ReactNode,
        onClick: () => {
          void handleUpdatePrimaryAction();
        },
        disabled: updateActionSubmitting,
        ariaLabel: '安装更新',
      };
    }

    if (sidebarUpdateStatus.status === 'downloading') {
      const hasPercent = typeof sidebarUpdateStatus.progressPercent === 'number';
      const percent = hasPercent ? Math.floor(sidebarUpdateStatus.progressPercent!) : 0;
      const percentText = hasPercent ? `（${percent}%）` : '';
      return {
        tooltip: `正在下载更新${percentText}`,
        label: hasPercent ? `下载中 ${percent}%` : '正在更新',
        statusClassName: 'main-layout__bottom-card--update-downloading',
        rightIcon: hasPercent
          ? React.createElement(Progress, { type: 'circle', percent, size: 16, strokeWidth: 10, strokeColor: '#9a5eed', showInfo: false })
          : React.createElement(LoadingOutlined, { spin: true }),
        onClick: undefined,
        disabled: true,
        ariaLabel: '正在下载更新',
      };
    }

    if (sidebarUpdateStatus.status === 'available') {
      return {
        tooltip: sidebarUpdateStatus.latestVersion
          ? `发现新版本 ${sidebarUpdateStatus.latestVersion}`
          : '发现新版本',
        label: '发现新版本',
        statusClassName: 'main-layout__bottom-card--update-available',
        rightIcon: React.createElement(SyncOutlined),
        onClick: () => setUpdateModalOpen(true),
        disabled: false,
        ariaLabel: '发现新版本',
      };
    }

    return null;
  })();

  // 浮层里的更新入口元数据（1:1 复制原始 MainLayout 逻辑）
  const profileUpdateMeta: ProfileUpdateMeta = (() => {
    if (pendingUpdateStatus?.status === 'downloaded') {
      return {
        label: '重启后完成更新',
        actionText: '立即重启',
        loading: false,
        clickable: true,
        onClick: () => {
          setProfilePopoverOpen(false);
          void handleUpdatePrimaryAction();
        },
      };
    }

    if (downloadingUpdateStatus?.status === 'downloading') {
      return {
        label: '正在更新...',
        actionText: '',
        loading: true,
        clickable: false,
        onClick: undefined,
      };
    }

    if (availableUpdateStatus?.status === 'available') {
      return {
        label: '检查到新版本',
        actionText: '立即更新',
        loading: false,
        clickable: true,
        onClick: () => {
          setProfilePopoverOpen(false);
          setUpdateModalOpen(true);
        },
      };
    }

    // 常驻"检查更新"入口，任何状态下都不隐藏
    return {
      label: '检查更新',
      actionText: '',
      loading: manualUpdateChecking,
      clickable: true,
      onClick: () => {
        setProfilePopoverOpen(false);
        void handleManualCheckUpdate();
      },
    };
  })();

  return {
    pendingUpdateStatus,
    effectiveUpdateStatus,
    updateModalOpen,
    setUpdateModalOpen,
    updateActionSubmitting,
    handleUpdatePrimaryAction,
    sidebarUpdateMeta,
    profileUpdateMeta,
  };
}
